import React from "react";
import { CodeBlock } from "../ui.js";

// /document-api?i=customer-kyc
// อ้างอิง ApiPartner: create customer ตอบ kyc_status + kyc_url (modules/customer/customer.service.js #withKyc)
// ฝาก/ถอน THB ที่ลูกค้ายังไม่ผ่าน KYC ตอบ P-4034 พร้อม kyc_url (services/deposits.service.js, withdrawals.service.js)
const STATUSES = [
  ["none", "The customer has not started identity verification yet.", "Send the customer to kyc_url."],
  ["pending", "The customer started verification but has not finished.", "Ask the customer to finish on kyc_url."],
  ["review", "Documents were submitted and are being reviewed.", "Wait. No action is needed."],
  ["rejected", "Verification was rejected.", "The customer must verify again on kyc_url."],
  ["expired", "The previous verification has expired.", "The customer must verify again on kyc_url."],
  ["bank_required", "Identity is verified but no bank account has been added yet.", "Ask the customer to add a bank account on kyc_url."],
  ["approved", "Verification is complete. Deposits and withdrawals are allowed.", "None. kyc_url is null."],
  ["not_required", "KYC is not required for this customer (for example LAK customers).", "None. kyc_url is null."],
];

const CREATE_EXAMPLE = `{
  "status": { "code": "S-2000", "message": "Success" },
  "data": {
    "customer_id": "00000000-0000-0000-0000-000000000000",
    ...
    "kyc_status": "none",
    "kyc_url": "https://swiftgoldpay.com/kyc?t=eyJyZWYiOi..."
  }
}`;

const DEPOSIT_EXAMPLE = `{
  "status": { "code": "P-4034", "message": "Customer KYC required" },
  "data": {
    "kyc_status": "pending",
    "kyc_url": "https://swiftgoldpay.com/kyc?t=eyJyZWYiOi..."
  }
}`;

export default function CustomerKyc() {
  return (
    <>
      <p>
        When KYC is enabled for your THB account, each customer must verify their
        identity and add a bank account before they can deposit or withdraw.
        Verification is done by the customer on a SwiftGoldPay page. You only
        need to send the customer the link we return.
      </p>

      <h5 className="fw-bold">1. Get the link when you create the customer</h5>
      <p>
        The <b>Create Customer</b> response includes <b>kyc_status</b> and{" "}
        <b>kyc_url</b>. A new customer always starts with <b>none</b>. Send the
        customer to <b>kyc_url</b> right away.
      </p>
      <CodeBlock text={CREATE_EXAMPLE} />
      <p>
        If the bank account is already registered, you receive <b>P-4004</b> with
        HTTP 200 and the existing customer, including its current{" "}
        <b>kyc_status</b> and a new <b>kyc_url</b>.
      </p>

      <h5 className="fw-bold">2. Deposits and withdrawals before KYC is complete</h5>
      <p>
        A THB deposit or withdrawal for a customer who has not finished KYC is
        rejected with <b>P-4034</b>. No transaction is created. The response
        contains the current status and a new link, so you can send the customer
        back to complete verification and then retry.
      </p>
      <CodeBlock text={DEPOSIT_EXAMPLE} />

      <h5 className="fw-bold">3. KYC status</h5>
      <div className="table-responsive">
        <table className="table table-striped-columns table-hover mb-3">
          <thead>
            <tr>
              <td className="text-nowrap">kyc_status</td>
              <td>Meaning</td>
              <td>What to do</td>
            </tr>
          </thead>
          <tbody>
            {STATUSES.map(([status, meaning, action]) => (
              <tr key={status}>
                <td className="text-nowrap">
                  <b>{status}</b>
                </td>
                <td>{meaning}</td>
                <td>{action}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h5 className="fw-bold">4. Notes</h5>
      <ul>
        <li>
          <b>kyc_url</b> is valid for <b>24 hours</b>. After that, get a new link
          from a P-4034 response or by creating the customer again with the same
          bank account (P-4004).
        </li>
        <li>
          <b>kyc_url</b> is <b>null</b> when the customer is already approved or
          KYC is not required.
        </li>
        <li>
          <b>kyc_status</b> may be <b>null</b> if the status could not be read at
          that moment. The customer is still created. The status is checked
          again on the first deposit or withdrawal.
        </li>
      </ul>
    </>
  );
}
